import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition
} from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  formateManifestFromBackend,
  FrontendFirmwarePackageDetails,
  mapBackendFirmwarePackageDataToFrontendFirmwareData,
  openConfirmDialog,
  PackageChangeHistoryDto,
  PageWrapper,
  StepperFormStore,
  UnsavedChangesGuardedComponent,
  useFirmwareStore
} from '@ottobock/remote-update/domain';
import { LogService } from '@ottobock/shared/domain';
import { codeToHtml } from 'shiki';

import { CreateFirmwarePackage } from '../firmware-modal/create-firmware-package';

import { ChangeHistoryTableComponent } from './change-history-table/change-history-table.component';
import { FirmwarePackageDetailsService } from './firmware-package-details.service';

@Component({
  selector: 'remote-update-feature-remote-update-dashboard-firmware-package-details',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatButtonModule,
    CommonModule,
    MatRadioModule,
    MatButtonToggleModule,
    FormsModule,
    MatTableModule,
    ChangeHistoryTableComponent,
    MatProgressBar,
    PageWrapper,
    MatCheckboxModule
  ],
  templateUrl: './firmware-package-details.html',
  styleUrl: './firmware-package-details.css'
})
export class FirmwarePackageDetails implements UnsavedChangesGuardedComponent, OnInit {
  route = inject(ActivatedRoute);
  private firmwareStore = inject(useFirmwareStore);
  private store = inject(StepperFormStore);
  firmwarePackageDetails = inject(FirmwarePackageDetailsService);
  packageId = 0;
  packageDetails = signal<FrontendFirmwarePackageDetails>({
    packageMetadata: { classification: '', packageName: '' },
    deviceCompatibility: { compatible_device_software_versions: [] },
    distribution: { access: '', supportedCompanionApps: [] },
    fileUpload: { fileName: '', fileSize: 0 },
    manifestInfo: { manifest: '' },
    releaseNotes: { notes: {} },
    packageStatus: ''
  });

  backRedirectRoute = '/firmware-package';

  packageChangeHistory = signal<PackageChangeHistoryDto[]>([]);

  private sanitizer = inject(DomSanitizer);
  extractedXmlContent = signal<SafeHtml | null>(null);
  dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  horizontalPosition: MatSnackBarHorizontalPosition = 'start';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';
  private logService = inject(LogService);
  private router = inject(Router);

  firmwareDetailsHasError = computed(
    () => this.firmwareStore.firmwarePackageDetails().error !== null
  );
  packageHistoryHasError = computed(() => this.firmwareStore.packageChangeHistory().error !== null);

  packageDetailIsLoading = computed(() => this.firmwareStore.firmwarePackageDetails().loading);
  packageHistoryIsLoading = computed(() => this.firmwareStore.packageChangeHistory().loading);

  pageIsLoading = computed(() => this.packageDetailIsLoading() || this.packageHistoryIsLoading());

  emptyFirmwarePackageTitle = 'No firmware package details';
  emptyFirmwarePackageDescription = 'This firmware has no details';
  unsavedChangesMessage =
    'If you leave now, your progress will be lost and the firmware package will not be edited.';

  emptyChangeHistoryTitle = 'No change history';
  emptyChangeHistoryDescription = 'This firmware has no change histories';
  height = 'min-h-0';

  async ngOnInit() {
    this.packageId = Number(this.route.snapshot.paramMap.get('id'));
    const details = await this.firmwarePackageDetails.getFirmwarePackageDetails(this.packageId);

    this.packageDetails.set(mapBackendFirmwarePackageDataToFrontendFirmwareData(details));

    const history = await this.firmwarePackageDetails.getPackageChangeHistory(this.packageId);
    this.packageChangeHistory.set(history);

    await this.renderHighlightedXml(this.packageDetails().manifestInfo.manifest);
  }
  private async renderHighlightedXml(xmlContent: string) {
    const prettyXml = formateManifestFromBackend(xmlContent);
    const highlightedXml = await codeToHtml(prettyXml, {
      lang: 'xml',
      theme: 'light-plus'
    });

    this.extractedXmlContent.set(this.sanitizer.bypassSecurityTrustHtml(highlightedXml));
  }

  confirmReleaseFirmwarePackage() {
    this.openConfirmReleasePackageDialog();
  }
  private openConfirmReleasePackageDialog() {
    const confirmDialog = openConfirmDialog(this.dialog, {
      message: 'Are you sure you want to release this firmware package?',
      rightButton: 'Release'
    });

    confirmDialog.afterClosed().subscribe(async (confirmed: boolean) => {
      if (confirmed) {
        try {
          await this.firmwarePackageDetails.releaseFirmwarePackage(this.packageId);

          const details = await this.firmwarePackageDetails.getFirmwarePackageDetails(
            this.packageId
          );
          this.packageDetails.set(mapBackendFirmwarePackageDataToFrontendFirmwareData(details));

          const history = await this.firmwarePackageDetails.getPackageChangeHistory(this.packageId);
          this.packageChangeHistory.set(history);

          this._snackBar.open('Firmware package released successfully!', 'Close');
        } catch (error: unknown) {
          this.logService.error('Failed to update state from Draft to Release', (error as Error).message);
          this._snackBar.open('Something went wrong. Try again.', 'Close');
        }
      }
    });
  }

  confirmDeleteFirmwarePackage() {
    this.openConfirmDeletePackageDialog();
  }

  private openConfirmDeletePackageDialog() {
    const confirmDialog = openConfirmDialog(this.dialog, {
      message: 'You are about to delete the draft of the firmware package. This cannot be undone.',
      rightButton: 'Delete',
      isDangerous: true
    });

    confirmDialog.afterClosed().subscribe(async (confirmed: boolean) => {
      if (confirmed) {
        try {
          await this.firmwarePackageDetails.deleteFirmwarePackage(this.packageId);
          this._snackBar.open('Firmware package deleted successfully!', 'Close');
          this.router.navigate(['/firmware-package']);
        } catch (error: unknown) {
          this.logService.error('Failed to delete firmware package', (error as Error).message);

          this._snackBar.open('Something went wrong. Try again.', 'Close');
        }
      }
    });
  }

  get hasUnsavedChanges() {
    return this.store.isFormDirty();
  }

  confirmWithdrawFirmwarePackage() {
    this.openConfirmWithdrawPackageDialog();
  }

  private openConfirmWithdrawPackageDialog() {
    const confirmDialog = openConfirmDialog(this.dialog, {
      message:
        'Are you sure you want to withdraw this firmware package?\n\nAll rollout plans that are associated with this firmware package will be invalidated and stopped.',
      rightButton: 'Withdraw',
      height: '272px'
    });

    confirmDialog.afterClosed().subscribe(async (confirmed: boolean) => {
      if (confirmed) {
        try {
          await this.firmwarePackageDetails.withdrawFirmwarePackage(this.packageId);

          const details = await this.firmwarePackageDetails.getFirmwarePackageDetails(
            this.packageId
          );
          this.packageDetails.set(mapBackendFirmwarePackageDataToFrontendFirmwareData(details));

          const history = await this.firmwarePackageDetails.getPackageChangeHistory(this.packageId);
          this.packageChangeHistory.set(history);

          this._snackBar.open('Firmware package withdrawn!', 'Close');
        } catch (error: unknown) {
          this.logService.error('Failed to withdraw firmware package', (error as Error).message);
          this._snackBar.open('Something went wrong. Try again.', 'Close');
        }
      }
    });
  }

  editFirmwarePackage() {
    this.store.setEditingFirmwarePackge(true);
    this.openDialog();
  }

  openDialog() {
    this.store.resetStore();
    const dialogRef = this.dialog.open(CreateFirmwarePackage, {
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      data: { packageDetails: this.packageDetails(), packageId: this.packageId },
      disableClose: true,
      closeOnNavigation: false,
      restoreFocus: false,
      panelClass: 'no-radius-dialog' 
    });

    dialogRef.afterClosed().subscribe(() => {
      this.store.resetStore();
    });
  }
}
